import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import { useAuthorization } from "@common/providers";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import {
  useGetProjectByIdQuery,
  useListProjectPricingsQuery,
  useListProjectServicesQuery,
  useListLineOfBusinessesForAccountQuery,
  useListProjectLocationsQuery,
  useListProjectGovernancesQuery,
  useListResourcesForAccountQuery,
  useListProjectResourcesForAccountQuery,
  useListPaymentTermsForAccountQuery,
  useUpdateProjectMutation,
} from "@generated";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import {
  capitalizeFirstLetter,
  renderResourceName,
  formatUnprocessibleResponse,
} from "@utils/helperFunctions";
import PricingCalculator from "../pricingUtils/PricingCalculator";
import Show from "./Show/Show";
import { PsPricingRow } from "./types/PsPricingRow";

interface Props {
  project_id: string;
  account_id: string;
}

// Build table rows from pricings matched to resources
const buildResourceRows = (
  pricings: any[],
  resources: any[],
  lobs: any[]
): PsPricingRow[] => {
  const rows: PsPricingRow[] = [];
  for (const resource of resources) {
    const matched = pricings.filter(
      (p) =>
        String(p.attributes["resource-id"]) === String(resource.id) &&
        p.attributes["resource-type"] === resource.type
    );
    if (matched.length === 0) continue;

    const hours = matched.reduce(
      (s, p) => s + (parseFloat(p.attributes["extended-hours"]) || 0),
      0
    );
    const revenue = matched.reduce(
      (s, p) => s + (parseFloat(p.attributes["contract-revenue"]) || 0),
      0
    );
    const cost = matched.reduce(
      (s, p) => s + (parseFloat(p.attributes["contract-cost"]) || 0),
      0
    );

    if (revenue === 0 && cost === 0) continue;

    rows.push({
      id: `res-${resource.type}-${resource.id}`,
      resource: renderResourceName(resource, lobs),
      hours,
      rate: hours > 0 ? revenue / hours : 0,
      revenue,
      effectiveCost: hours > 0 ? cost / hours : 0,
      totalCost: cost,
      grossProfit: revenue - cost,
      rowType: "data",
    });
  }
  return rows;
};

// Sum rows into a TOTAL row
const buildTotalRow = (
  rows: PsPricingRow[],
  adjustment: number,
  id = "total",
  label = "TOTAL"
): PsPricingRow => {
  const dataRows = rows.filter(
    (r) =>
      r.rowType === "data" ||
      r.rowType === "subservice" ||
      r.rowType === "serviceTotal"
  );
  const totalHours = dataRows.reduce((s, r) => s + r.hours, 0);
  const totalRevenue = dataRows.reduce((s, r) => s + r.revenue, 0) + adjustment;
  const totalCost = dataRows.reduce((s, r) => s + r.totalCost, 0);
  return {
    id,
    resource: label,
    hours: totalHours,
    rate: 0,
    revenue: totalRevenue,
    effectiveCost: 0,
    totalCost,
    grossProfit: totalRevenue - totalCost,
    rowType: "total",
  };
};

const PsPricingV2Mspa: React.FC<Props> = ({ project_id, account_id }) => {
  const projectId = project_id;
  const { accountSlug } = useSelector((state: RootState) => state.slug);

  const { data: project, isLoading: projectLoading } = useGetProjectByIdQuery({
    id: projectId as any,
    slug: accountSlug,
    include: ["project-phases", "payment-term"],
  } as any);

  const { data: pricingsData, isLoading: pricingsLoading } =
    useListProjectPricingsQuery({
      slug: accountSlug,
      id: Number(projectId),
    });

  const { data: lobsData, isLoading: lobsLoading } =
    useListLineOfBusinessesForAccountQuery({ slug: accountSlug });

  const { data: locationsData, isLoading: locationsLoading } =
    useListProjectLocationsQuery({
      slug: accountSlug,
      filter: { project: projectId },
    } as any);

  const { data: governancesData, isLoading: governancesLoading } =
    useListProjectGovernancesQuery({
      slug: accountSlug,
      filter: { project: projectId },
      include: ["project-resource", "project-phase"],
    } as any);

  const { data: resourcesData, isLoading: resourcesLoading } =
    useListResourcesForAccountQuery({
      slug: accountSlug,
      page: { size: 500 },
    } as any);

  const { data: projectResourcesData, isLoading: projectResourcesLoading } =
    useListProjectResourcesForAccountQuery({
      slug: accountSlug,
      filter: { project: projectId },
      include: ["line-of-business"],
      page: { size: 500 },
    });

  const { data: servicesData, isLoading: servicesLoading } =
    useListProjectServicesQuery({
      slug: accountSlug,
      filter: {
        project: projectId,
        "service-type": "professional_services",
      },
      include: ["project-subservices", "project-subservices.resource"] as any,
      page: { size: 500 },
    });

  const { data: paymentTermsData } = useListPaymentTermsForAccountQuery({
    slug: accountSlug,
  });

  const authorized = useAuthorization();
  const { hasPrivilege } = usePrivilegeCheck();
  const pricingPrivilege = hasPrivilege("projects.pricing_adjustment", [
    "manage",
  ]);

  const [updateProject] = useUpdateProjectMutation();
  const [adjustmentError, setAdjustmentError] = useState<string[] | null>(null);
  const [paymentTermError, setPaymentTermError] = useState<string[] | null>(
    null
  );
  const [newPaymentTermId, setNewPaymentTermId] = useState<string | null>(null);

  const isLoading =
    !authorized ||
    projectLoading ||
    pricingsLoading ||
    lobsLoading ||
    locationsLoading ||
    governancesLoading ||
    resourcesLoading ||
    projectResourcesLoading ||
    servicesLoading;

  const attr = (project?.data?.attributes as any) || {};
  const currencyUnit = attr["field-labels"]?.currency_unit || "$";
  const projectStatus = attr.status || "";
  const oneTimeAdjustment = parseFloat(attr["price-adjustment"]) || 0;
  const paymentInfo = attr["payment-info"] || {};
  const rateTypeRaw = paymentInfo["rate-type"] || "hours";
  const rateType =
    rateTypeRaw !== "hours"
      ? capitalizeFirstLetter(rateTypeRaw) + "s"
      : capitalizeFirstLetter(rateTypeRaw);

  const allPricings: any[] = (pricingsData?.data as any[]) || [];
  const lobs: any[] = (lobsData?.data as any[]) || [];
  const locations: any[] = (locationsData?.data as any[]) || [];
  const governances: any[] = (governancesData?.data as any[]) || [];
  const accountResources: any[] = (resourcesData?.data as any[]) || [];
  const rawProjectResources: any[] =
    (projectResourcesData?.data as any[]) || [];
  const allServices: any[] = (servicesData?.data as any[]) || [];
  const includedRecords: any[] = (servicesData?.included as any[]) || [];
  const phases: any[] =
    ((project as any)?.included || []).filter(
      (item: any) => item.type === "project-phases"
    ) || [];
  const paymentTerms: any[] = (paymentTermsData?.data as any[]) || [];

  // Legacy code sets project resource type to "ProjectResource" and
  // concatenates with account resources for matching against pricings
  const projResources = rawProjectResources.map((r) => ({
    ...r,
    type: "ProjectResource",
  }));
  const projectResources: any[] = projResources.concat(accountResources);

  const professionalPricings = allPricings.filter(
    (p) => p.attributes["service-type"] === "professional"
  );
  const governancePricings = allPricings.filter(
    (p) => p.id.split("-")[1] === "governances"
  );

  const subservices = includedRecords.filter(
    (item) => item.type === "project-subservices"
  );

  // Current payment term
  const currentPaymentTermId =
    (project?.data as any)?.relationships?.["payment-term"]?.data?.id || null;
  const currentPaymentTerm = paymentTerms.find(
    (t) => String(t.id) === String(currentPaymentTermId)
  );

  // Initialize form state once data is available
  const [formInitialized, setFormInitialized] = useState(false);
  if (!isLoading && !formInitialized) {
    setNewPaymentTermId(currentPaymentTermId);
    setFormInitialized(true);
  }

  // ===== BY LOB =====
  const byLobData = useMemo(() => {
    if (isLoading) return { profRows: [], lobTables: [], govRows: [] };

    const lobsWithPricings = lobs.filter((lob) =>
      professionalPricings.some(
        (p) => p.attributes["lob-name"] === lob.attributes.name
      )
    );

    const allProfRows = buildResourceRows(
      professionalPricings,
      projectResources,
      lobs
    );
    const profTotal = buildTotalRow(allProfRows, oneTimeAdjustment);

    const calc = new PricingCalculator(
      "professional",
      oneTimeAdjustment,
      professionalPricings.length > 0
        ? professionalPricings
        : [
            {
              attributes: {
                "billing-frequency": "one_time",
                "service-revenue": "0",
                "contract-revenue": "0",
                "contract-cost": "0",
              },
            },
          ],
      0
    );
    const marginRow: PsPricingRow = {
      id: "margin",
      resource: "MARGIN",
      hours: 0,
      rate: 0,
      revenue: parseFloat(calc.margin) || 0,
      effectiveCost: 0,
      totalCost: 0,
      grossProfit: 0,
      rowType: "margin",
    };

    const profRows = [...allProfRows, profTotal, marginRow];

    const lobTables = lobsWithPricings.map((lob) => {
      const lobPricings = professionalPricings.filter(
        (p) => p.attributes["lob-name"] === lob.attributes.name
      );
      const lobRows = buildResourceRows(lobPricings, projectResources, lobs);
      const lobTotal = buildTotalRow(lobRows, 0, `total-lob-${lob.id}`);
      return {
        title: `${lob.attributes.name} Pricing`,
        rows: [...lobRows, lobTotal],
      };
    });

    const govRows = buildResourceRows(
      governancePricings,
      projectResources,
      lobs
    );
    const govTotal = buildTotalRow(govRows, 0, "total-gov");

    return {
      profRows,
      lobTables,
      govRows: [...govRows, govTotal],
    };
  }, [
    isLoading,
    professionalPricings,
    governancePricings,
    projectResources,
    lobs,
    oneTimeAdjustment,
  ]);

  // ===== BY PHASE =====
  const byPhaseData = useMemo(() => {
    if (isLoading) return { phaseTables: [], govRows: [] };

    const phasesWithPricings = phases.filter((phase) =>
      professionalPricings.some(
        (p) => p.attributes.phase?.name === phase.attributes.name
      )
    );

    const phaseTables = phasesWithPricings.map((phase) => {
      const phasePricings = professionalPricings.filter(
        (p) => p.attributes.phase?.name === phase.attributes.name
      );
      const phaseRows = buildResourceRows(
        phasePricings,
        projectResources,
        lobs
      );
      const phaseTotal = buildTotalRow(phaseRows, 0, `total-phase-${phase.id}`);
      return {
        title: `${phase.attributes.name} Pricing`,
        rows: [...phaseRows, phaseTotal],
      };
    });

    // Governance pricings already shown in a phase section must be excluded
    // from the Project Governance section to avoid duplication.
    const projectLevelGovernancePricings = governancePricings.filter(
      (p) => !p.attributes.phase?.name
    );

    const govRows = buildResourceRows(
      projectLevelGovernancePricings,
      projectResources,
      lobs
    );
    const govTotal = buildTotalRow(govRows, 0, "total-gov-phase");

    return {
      phaseTables,
      govRows: [...govRows, govTotal],
    };
  }, [
    isLoading,
    professionalPricings,
    governancePricings,
    phases,
    projectResources,
    lobs,
  ]);

  // ===== BY SERVICE =====
  const byServiceData = useMemo(() => {
    if (isLoading) return { phaseTables: [], govRows: [] };

    const servicePricings = allPricings.filter((p) =>
      p.id.includes("project-services")
    );
    const subservicePricings = allPricings.filter((p) =>
      p.id.includes("project-subservices")
    );

    const phasesWithServices = phases.filter((phase) =>
      servicePricings
        .concat(subservicePricings)
        .some((p) => p.attributes.phase?.name === phase.attributes.name)
    );

    const phaseTables = phasesWithServices.map((phase) => {
      const phaseName = phase.attributes.name;
      const pricingsPerPhase = allPricings.filter(
        (p) => p.attributes.phase?.name === phaseName
      );

      const rows: PsPricingRow[] = [];
      let grandTotalHours = 0;
      let grandTotalRevenue = 0;
      let grandTotalCost = 0;

      for (const service of allServices) {
        const svcPricings = pricingsPerPhase.filter(
          (p) =>
            p.id.split("-")[1] === "services" &&
            String(p.attributes["source-id"]) === String(service.id)
        );
        if (svcPricings.length === 0) continue;

        const svcName = service.attributes.name;
        const svcHours = svcPricings.reduce(
          (s, p) => s + (parseFloat(p.attributes["extended-hours"]) || 0),
          0
        );
        const svcRevenue = svcPricings.reduce(
          (s, p) => s + (parseFloat(p.attributes["service-revenue"]) || 0),
          0
        );
        const svcCost = svcPricings.reduce(
          (s, p) => s + (parseFloat(p.attributes["service-cost"]) || 0),
          0
        );

        rows.push({
          id: `svc-${phase.id}-${service.id}`,
          resource: svcName,
          hours: svcHours,
          rate: svcHours > 0 ? svcRevenue / svcHours : 0,
          revenue: svcRevenue,
          effectiveCost: svcHours > 0 ? svcCost / svcHours : 0,
          totalCost: svcCost,
          grossProfit: svcRevenue - svcCost,
          rowType: "data",
        });

        const subRels =
          service.relationships?.["project-subservices"]?.data || [];
        let hasSubservices = false;
        let serviceTotalHours = svcHours;
        let serviceTotalRevenue = svcRevenue;
        let serviceTotalCost = svcCost;

        for (const rel of subRels) {
          const sub = subservices.find((s) => String(s.id) === String(rel.id));
          if (!sub) continue;

          const subPricings = pricingsPerPhase.filter(
            (p) =>
              p.id.split("-")[1] === "subservices" &&
              String(p.attributes["source-id"]) === String(sub.id)
          );
          if (subPricings.length === 0) continue;

          hasSubservices = true;
          const subName = sub.attributes.name;
          const subHours = subPricings.reduce(
            (s, p) => s + (parseFloat(p.attributes["extended-hours"]) || 0),
            0
          );
          const subRevenue = subPricings.reduce(
            (s, p) => s + (parseFloat(p.attributes["service-revenue"]) || 0),
            0
          );
          const subCost = subPricings.reduce(
            (s, p) => s + (parseFloat(p.attributes["service-cost"]) || 0),
            0
          );

          serviceTotalHours += subHours;
          serviceTotalRevenue += subRevenue;
          serviceTotalCost += subCost;

          rows.push({
            id: `subsvc-${phase.id}-${sub.id}`,
            resource: subName,
            hours: subHours,
            rate: subHours > 0 ? subRevenue / subHours : 0,
            revenue: subRevenue,
            effectiveCost: subHours > 0 ? subCost / subHours : 0,
            totalCost: subCost,
            grossProfit: subRevenue - subCost,
            rowType: "subservice",
          });
        }

        if (hasSubservices) {
          rows.push({
            id: `subtotal-${phase.id}-${service.id}`,
            resource: "",
            hours: serviceTotalHours,
            rate:
              serviceTotalHours > 0
                ? serviceTotalRevenue / serviceTotalHours
                : 0,
            revenue: serviceTotalRevenue,
            effectiveCost:
              serviceTotalHours > 0 ? serviceTotalCost / serviceTotalHours : 0,
            totalCost: serviceTotalCost,
            grossProfit: serviceTotalRevenue - serviceTotalCost,
            rowType: "subtotal",
          });
        }

        grandTotalHours += serviceTotalHours;
        grandTotalRevenue += serviceTotalRevenue;
        grandTotalCost += serviceTotalCost;
      }

      rows.push({
        id: `total-svc-phase-${phase.id}`,
        resource: `${phaseName} Total`,
        hours: grandTotalHours,
        rate: 0,
        revenue: grandTotalRevenue,
        effectiveCost: 0,
        totalCost: grandTotalCost,
        grossProfit: grandTotalRevenue - grandTotalCost,
        rowType: "total",
      });

      return {
        title: `${phaseName} Pricing`,
        rows,
      };
    });

    const govRows = buildGovernanceRows(governancePricings);
    const govTotal = buildTotalRow(govRows, 0, "total-gov-svc");

    return {
      phaseTables,
      govRows: [...govRows, govTotal],
    };
  }, [
    isLoading,
    allPricings,
    allServices,
    subservices,
    phases,
    governancePricings,
    projectResources,
    lobs,
  ]);

  // ===== BY LOCATION =====
  const byLocationData = useMemo(() => {
    if (isLoading) return { locTables: [] };

    const locsWithPricings = locations.filter((loc) =>
      professionalPricings.some((p) => p.attributes.location?.id == loc.id)
    );

    const locTables = locsWithPricings.map((loc) => {
      const a = loc.attributes;
      const parts = [
        a.name ? `${a.name}:` : "",
        a.street,
        a.street2,
        a.city,
        a.state,
        a["postal-code"],
        a.country?.toUpperCase(),
      ].filter(Boolean);
      const locTitle = parts.join(" ");

      const locPricings = professionalPricings.filter(
        (p) => p.attributes.location?.id == loc.id
      );
      const locRows = buildResourceRows(locPricings, projectResources, lobs);
      const locTotal = buildTotalRow(locRows, 0, `total-loc-${loc.id}`);
      return {
        title: locTitle,
        rows: [...locRows, locTotal],
      };
    });

    return { locTables };
  }, [isLoading, professionalPricings, locations, projectResources, lobs]);

  // Determine which tabs to show
  const servicePricingsForTab = allPricings.filter((p) =>
    p.id.includes("project-services")
  );
  const showServiceTab =
    servicePricingsForTab.filter(
      (p) => p.attributes["service-type"] === "professional"
    ).length > 0;
  const showLocationTab = locations.length >= 2;

  if (isLoading) {
    return <ScopeStackSpinner />;
  }

  const handlePaymentTermSubmit = async () => {
    if (!newPaymentTermId) return;
    try {
      await updateProject({
        slug: accountSlug,
        id: Number(projectId),
        body: {
          data: {
            type: "projects",
            id: projectId,
            relationships: {
              account: { data: { type: "accounts", id: account_id } },
              "payment-term": {
                data: { id: newPaymentTermId, type: "payment-terms" },
              },
            },
          } as any,
        },
      }).unwrap();
      window.location.reload();
    } catch (err: any) {
      setPaymentTermError(
        formatUnprocessibleResponse(
          err,
          "pricing details"
        ) as unknown as string[]
      );
    }
  };

  return (
    <div className="tab-content">
      <div className="tab-pane active">
        <Show
          currencyUnit={currencyUnit}
          rateType={rateType}
          projectId={projectId}
          projectStatus={projectStatus}
          pricingPrivilege={pricingPrivilege}
          oneTimeAdjustment={oneTimeAdjustment}
          adjustmentError={adjustmentError}
          setAdjustmentError={setAdjustmentError}
          paymentTerms={paymentTerms}
          currentPaymentTerm={currentPaymentTerm}
          newPaymentTermId={newPaymentTermId}
          setNewPaymentTermId={setNewPaymentTermId}
          onSubmitPaymentTerm={handlePaymentTermSubmit}
          paymentTermError={paymentTermError}
          setPaymentTermError={setPaymentTermError}
          byLobData={byLobData}
          byPhaseData={byPhaseData}
          byServiceData={byServiceData}
          byLocationData={byLocationData}
          showServiceTab={showServiceTab}
          showLocationTab={showLocationTab}
          isLoading={false}
        />
      </div>
    </div>
  );
};

// Build governance rows directly from pricing attributes
const buildGovernanceRows = (pricings: any[]): PsPricingRow[] =>
  pricings
    .map((p) => {
      const attr = p.attributes;
      const hours = parseFloat(attr["extended-hours"]) || 0;
      const revenue = parseFloat(attr["contract-revenue"]) || 0;
      const cost = parseFloat(attr["contract-cost"]) || 0;
      if (revenue === 0 && cost === 0) return null;
      return {
        id: `gov-${p.id}`,
        resource: attr.name || "",
        hours,
        rate: parseFloat(attr["hourly-rate"]) || 0,
        revenue,
        effectiveCost: parseFloat(attr["hourly-cost"]) || 0,
        totalCost: cost,
        grossProfit: revenue - cost,
        rowType: "data" as const,
      };
    })
    .filter(Boolean) as PsPricingRow[];

export default PsPricingV2Mspa;
